import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, ArrowLeftRight, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { GlassCard, GradButton, fmtMoney } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";

const TIPLER = [{ value: "musteri", label: "Müşteri" }, { value: "tedarikci", label: "Tedarikçi" }, { value: "her_ikisi", label: "Müşteri / Tedarikçi" }, { value: "broker", label: "Broker" }, { value: "kamu", label: "Kamu Kuruluşu" }];
const YONLER = [{ value: "tipe", label: "Cari tipine göre (müşteri bize, tedarikçiye biz)" }, { value: "arti", label: "Hepsi bize borçlu" },
  { value: "eksi", label: "Hepsine biz borçluyuz" }, { value: "yok", label: "Bakiyeleri aktarma" }];
const carpan = (yon, tip) => (yon === "yok" ? 0 : yon === "arti" ? 1 : yon === "eksi" ? -1 : tip === "tedarikci" ? -1 : 1);

const Bakiye = ({ s, yon, onCevir }) => {
  const v = s.bakiye * carpan(yon, s.tip) * (s.ters ? -1 : 1);
  if (yon === "yok" || !s.bakiye) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <button data-testid={`kb-yon-${s.satir}`} onClick={onCevir} title="Yönü çevir" className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10">
      <ArrowLeftRight className="w-3 h-3" /><span className={v >= 0 ? "text-emerald-300" : "text-red-400"}>{v >= 0 ? "Alacaklıyız" : "Borçluyuz"}</span>
      <span className="font-mono">{fmtMoney(Math.abs(v))}</span>
    </button>
  );
};

const Satir = ({ s, yon, set }) => {
  const islemler = s.eslesen ? [{ value: "guncelle", label: "Bilgilerini güncelle" }, { value: "yeni", label: "Yeni kayıt oluştur" }, { value: "atla", label: "Atla" }]
    : [{ value: "yeni", label: "Yeni kayıt" }, { value: "atla", label: "Atla" }];
  const islem = (v) => set(v === "yeni" && s.eslesen?.neden === "Aynı cari kodu" ? { islem: v, kod: "" } : { islem: v, kod: v === "guncelle" ? s.kod_ilk : s.kod });
  return (
    <div className={`grid md:grid-cols-[110px_1fr_150px_200px_180px] gap-2 items-center px-3 py-2.5 ${s.islem === "atla" ? "opacity-50" : ""}`} data-testid={`kb-satir-${s.satir}`}>
      <Input data-testid={`kb-kod-${s.satir}`} value={s.kod} placeholder="otomatik" disabled={s.islem === "guncelle"} onChange={(e) => set({ kod: e.target.value })} className="h-8 text-xs font-mono bg-white/5" />
      <div className="min-w-0 space-y-1">
        <Input data-testid={`kb-unvan-${s.satir}`} value={s.unvan} onChange={(e) => set({ unvan: e.target.value })} className="h-8 text-sm bg-white/5" />
        {s.eslesen && <div className="flex items-center gap-1.5 text-[11px] text-amber-300" data-testid={`kb-eslesen-${s.satir}`}><AlertTriangle className="w-3 h-3 shrink-0" /><span className="truncate">{s.eslesen.neden}: {s.eslesen.kod ? `${s.eslesen.kod} · ` : ""}{s.eslesen.unvan}</span></div>}
      </div>
      <div className="space-y-1"><SelectBox testid={`kb-tip-${s.satir}`} value={s.tip} onChange={(v) => set({ tip: v })} options={TIPLER} />{s.kb_tip && <div className="text-[10px] text-muted-foreground">KolayBi: {s.kb_tip}</div>}</div>
      <div>{s.islem === "guncelle" ? <span className="text-[11px] text-muted-foreground">Bakiyeye dokunulmaz</span> : <Bakiye s={s} yon={yon} onCevir={() => set({ ters: !s.ters })} />}</div>
      <SelectBox testid={`kb-islem-${s.satir}`} value={s.islem} onChange={islem} options={islemler} />
    </div>
  );
};

const Sonuc = ({ r }) => (
  <GlassCard className="p-5" data-testid="kb-sonuc">
    <div className="text-sm"><b className="text-emerald-300" data-testid="kb-eklenen">{r.eklenen}</b> yeni · <b data-testid="kb-guncellenen">{r.guncellenen}</b> güncellendi · {r.atlanan} atlandı · <b className={r.hatali ? "text-red-300" : ""} data-testid="kb-hatali">{r.hatali}</b> hatalı</div>
    {r.hatalar.length > 0 && <div className="mt-3 max-h-48 overflow-y-auto erp-scroll text-xs space-y-1">{r.hatalar.map((h) => <div key={h.satir}><span className="font-mono text-muted-foreground mr-2">Satır {h.satir}</span>{h.unvan}: {h.mesaj}</div>)}</div>}
  </GlassCard>
);

export const KolaybiCari = () => {
  const [liste, setListe] = useState(null);
  const [yon, setYon] = useState("tipe");
  const [filtre, setFiltre] = useState("hepsi");
  const [busy, setBusy] = useState(false);
  const [sonuc, setSonuc] = useState(null);

  const yukle = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("dosya", file); setBusy(true); setSonuc(null);
    try {
      const { data } = await api.post("/aktarim-kolaybi/cari/onizle", fd);
      setListe(data.satirlar.map((s) => ({ ...s, islem: s.eslesen ? "guncelle" : "yeni", ters: false, kod_ilk: s.kod })));
      toast.success(`${data.satirlar.length} cari okundu`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (satir, p) => setListe((l) => l.map((s) => (s.satir === satir ? { ...s, ...p } : s)));
  const eslesen = useMemo(() => (liste || []).filter((s) => s.eslesen).length, [liste]);
  const gorunen = (liste || []).filter((s) => filtre === "hepsi" || (filtre === "eslesen" ? s.eslesen : !s.eslesen));

  const aktar = async () => {
    setBusy(true);
    try {
      const satirlar = liste.map((s) => ({ ...s, acilis_bakiye: s.bakiye * carpan(yon, s.tip) * (s.ters ? -1 : 1), eslesen_id: s.eslesen?.id || null }));
      const { data } = await api.post("/aktarim-kolaybi/cari/aktar", { satirlar });
      setSonuc(data); toast.success(`${data.eklenen} cari eklendi, ${data.guncellenen} güncellendi`);
      if (!data.hatali) setListe(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="kb-cari">
      <GlassCard className="p-6 space-y-4">
        <div>
          <h3 className="font-head font-semibold">KolayBi · Genel Cari Hesapları</h3>
          <p className="text-xs text-muted-foreground mt-1">KolayBi &gt; Cari Hesaplar &gt; Dışa aktar ile indirdiğiniz Excel'i yükleyin. Önce önizleme gelir; sistemde aynı kod, vergi no veya ünvanla eşleşenleri düzenleyip, yeniden adlandırıp ya da yeni kayıt olarak açabilirsiniz.</p>
        </div>
        <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm grad-bg text-white cursor-pointer" data-testid="kb-dosya-label">
          <Upload className="w-4 h-4" />{busy && !liste ? "Okunuyor…" : "Excel dosyasını seç"}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="kb-dosya" onChange={(e) => { yukle(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </GlassCard>
      {liste && (
        <GlassCard className="p-4 space-y-3" data-testid="kb-onizleme">
          <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="max-w-xl"><div className="text-xs text-muted-foreground mb-1">Bakiye yönü (satırdaki düğmeyle tek tek de çevirebilirsiniz)</div><SelectBox testid="kb-yon" value={yon} onChange={(v) => { setYon(v); setListe((l) => l.map((s) => ({ ...s, ters: false }))); }} options={YONLER} /></div>
            <div className="flex gap-1.5">{[["hepsi", `Tümü (${liste.length})`], ["eslesen", `Eşleşenler (${eslesen})`], ["yeni", `Yeni (${liste.length - eslesen})`]].map(([k, l]) => (
              <button key={k} data-testid={`kb-filtre-${k}`} onClick={() => setFiltre(k)} className={`px-3 py-1.5 rounded-full text-xs ${filtre === k ? "grad-bg text-white" : "bg-white/5 text-muted-foreground hover:text-white"}`}>{l}</button>
            ))}</div>
          </div>
          <div className="max-h-[520px] overflow-y-auto erp-scroll divide-y divide-white/5 rounded-xl border border-white/10" data-testid="kb-liste">
            {gorunen.map((s) => <Satir key={s.satir} s={s} yon={yon} set={(p) => set(s.satir, p)} />)}
          </div>
          <div className="flex justify-end"><GradButton data-testid="kb-aktar" onClick={aktar} disabled={busy}>{busy ? "Aktarılıyor…" : `${liste.filter((s) => s.islem !== "atla").length} cariyi aktar`}</GradButton></div>
        </GlassCard>
      )}
      {sonuc && <Sonuc r={sonuc} />}
    </div>
  );
};
